import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class StorageService {
  appData$ = new BehaviorSubject<any>(null);

  constructor() {
    // Initialize with current sessionStorage data
    const currentApp = this.getItem<any>("app");
    this.appData$.next(currentApp);
  }

  /**
   * Set an item in sessionStorage
   * @param key The storage key.
   * @param value The value to store (will be stringified if object).
   */
  setItem(key: string, value: any): void {
    try {
      const data = typeof value === "string" ? value : JSON.stringify(value);
      sessionStorage.setItem(key, data);

      // Emit changes for app data
      if (key === "app") {
        this.appData$.next(value);
      }
    } catch (error) {
      console.error("Error saving to sessionStorage", error);
    }
  }

  /**
   * Get an item from sessionStorage
   * @param key The storage key.
   * @returns The parsed value or null if not found.
   */
  getItem<T>(key: string): T | null {
    const data = sessionStorage.getItem(key);
    if (data === null) return null;

    const trimmed = data.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return JSON.parse(data) as T;
      } catch (error) {
        console.warn("Error parsing sessionStorage item as JSON", error);
      }
    }

    return data as T;
  }

  /**
   * Remove an item from sessionStorage
   * @param key The storage key.
   */
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Clear all sessionStorage items.
   */
  clear(): void {
    sessionStorage.clear();
  }

  /**
   * Check if a key exists in sessionStorage
   * @param key The storage key.
   * @returns true if the key exists, false otherwise.
   */
  hasKey(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Get all keys in sessionStorage
   * @returns An array of keys.
   */
  getAllKeys(): string[] {
    return Object.keys(sessionStorage);
  }
}
